// CacheShare™ allocation model (Coach Mode V2, pure functions).
//
// The calculators score a roster and split the revenue-share budget across
// it. The split the coach works with (both pools, with locked amounts and
// hidden players) comes from the allocate endpoint, which takes the board's
// rows as they are (allocateRequestOf, mergeAllocation), so a lock, a hide
// or a NIL budget never needs another calculator round trip. The Team and
// All players columns, filters and pages are derived here.

import { classificationOf as classWordOf } from "../../ForCoaches/v2/coachUtils";

export const EMPTY = "-";

export const rowId = (r) => String(r?.sportsRadarId || r?.cerebroId || r?.id || "");

export const parseMoney = (value) => {
  const n = Number(String(value ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

export const fmtMoney2 = (v) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return EMPTY;
  const n = Number(v);
  const abs = Math.abs(n) < 0.005 ? 0 : Math.abs(n);
  return `${n <= -0.005 ? "-" : ""}$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// What the Remaining figure means. The allocate endpoint splits in whole
// cents, so a fully allocated pool is exactly $0.00, and only its
// overAllocated flag (locks above the budget, never rounding) reads as
// "exceeds budget".
export const remainingNote = (remaining, over = false) => {
  if (over) return "Allocation exceeds budget";
  if (Math.abs(remaining) < 0.005) return "fully allocated";
  return "left to allocate";
};

// A budget as the session stores it: "10,000,000.00".
export const amountText = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Budget inputs show two decimals once the field is left.
export const padAmount = (text) => {
  const v = String(text || "").trim();
  if (!v || parseMoney(v) === 0) return "0.00";
  const [whole, decimals = ""] = v.split(".");
  return `${whole}.${`${decimals}00`.slice(0, 2)}`;
};

// Split `total` across `weights` in whole cents so the parts add up to the
// total exactly (largest remainder). All-zero weights split evenly, like the
// calculators do.
export const splitByWeight = (total, weights) => {
  const cents = Math.round(total * 100);
  if (!weights.length || cents <= 0) return weights.map(() => 0);
  const clean = weights.map((w) => (Number(w) > 0 ? Number(w) : 0));
  const sum = clean.reduce((s, w) => s + w, 0);
  const shares = sum > 0 ? clean : clean.map(() => 1);
  const shareSum = sum > 0 ? sum : clean.length;
  const raw = shares.map((w) => (cents * w) / shareSum);
  const parts = raw.map(Math.floor);
  const left = cents - parts.reduce((s, v) => s + v, 0);
  raw
    .map((v, i) => [v - parts[i], i])
    .sort((a, b) => b[0] - a[0] || a[1] - b[1])
    .slice(0, left)
    .forEach(([, i]) => {
      parts[i] += 1;
    });
  return parts.map((c) => c / 100);
};

// The table's one roster: the calculator's rows and the imported players
// (tagged "Added"), by sport score. Every split runs over this order, since
// tied weights hand their leftover cents out by position.
export const mergeRows = (roster, added) => [...roster, ...added.map((p) => ({ ...p, added: true }))].sort((a, b) => (b.sportScore || 0) - (a.sportScore || 0));

// The revenue share Team and All players columns, and who is hidden from
// which pool. The calculator's split (at the distributed budget) is the
// weight, so hiding a player hands their money back to the pot and the
// visible players share it in the same proportions. For the exponential
// basketball split this equals re-running the calculator on the subset.
// The Allocated and NIL figures come from the allocate endpoint
// (mergeAllocation).
export const applyAllocation = (rows, { rsBudget = 0, hiddenRs = new Set(), hiddenNil = new Set() } = {}) => {
  const rsRows = rows.filter((r) => !hiddenRs.has(rowId(r)));
  const team = splitByWeight(rsBudget, rsRows.map((r) => r.payoutTeam));
  const all = splitByWeight(rsBudget, rsRows.map((r) => r.payoutAllPlayers));
  const rsById = new Map(rsRows.map((r, i) => [rowId(r), { payoutTeam: team[i], payoutAllPlayers: all[i] }]));
  return rows.map((r) => {
    const rs = rsById.get(rowId(r));
    return {
      ...r,
      hiddenRs: !rs,
      hiddenNil: hiddenNil.has(rowId(r)),
      payoutTeam: rs && rsBudget > 0 ? rs.payoutTeam : null,
      payoutAllPlayers: rs && rsBudget > 0 ? rs.payoutAllPlayers : null,
    };
  });
};

// The session saves a locked amount only below this, and refuses the whole
// team slice for one at or above it.
export const MAX_LOCK = 1e13;

// A lock map as the session stores it ({playerId: amount}): amounts from 0
// to under MAX_LOCK, in whole cents.
export const locksOf = (raw) =>
  Object.fromEntries(
    Object.entries(raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {})
      .filter(([id, amount]) => id && typeof amount === "number" && Number.isFinite(amount) && amount >= 0)
      .map(([id, amount]) => [id, round2(amount)])
      .filter(([, amount]) => amount < MAX_LOCK),
  );

// The allocate endpoint's request for the board's rows (mergeRows order,
// which its cent tie-break follows), hidden or not. Each pool sends its
// distributed budget, its hidden players and the coach's locks; a pool with
// no budget is null. Revenue share sends the calculator's own payoutTeam,
// the weight the unlocked players share the rest by, and each player's
// floor (rsFloor, the low end of his PerformanceValue), the minimum his
// Allocated is raised to unless he is locked; a row a calculator sent
// without one (an older answer, a saved player) sends no rsFloor and the
// endpoint floors it on its CacheValue as before. NIL sends what the NIL
// model scores (the IRL score among it) and each player's NILx value, the
// minimum his NIL Allocated is raised to.
const floorOf = (r) => (r.rsFloor === undefined ? {} : { rsFloor: r.rsFloor });

export const allocateRequestOf = (rows, { sport = "", rsBudget = 0, nilBudget = 0, hiddenRs = new Set(), hiddenNil = new Set(), rsLocks = {}, nilLocks = {} } = {}) => {
  const ids = rows.map(rowId);
  const pool = (budget, hidden, locks, fields) =>
    budget > 0 ? { budget, rows: rows.map((r, i) => ({ id: ids[i], ...fields(r) })), hidden: ids.filter((id) => hidden.has(id)), locks } : null;
  return {
    sport,
    revshare: pool(rsBudget, hiddenRs, rsLocks, (r) => ({ payoutTeam: r.payoutTeam ?? null, cacheValue: r.cacheValue ?? null, ...floorOf(r) })),
    nil: pool(nilBudget, hiddenNil, nilLocks, (r) => ({
      position: r.position || "",
      positionGroup: r.positionGroup || null,
      rating: r.rating ?? null,
      sportScore: r.sportScore ?? null,
      visibilityScore: r.visibilityScore ?? null,
      irlScore: r.irlScore ?? null,
      nilxValue: r.nilxValue ?? null,
    })),
  };
};

// One player's figure in one pool: the allocate answer's amount, or the
// coach's own lock, shown at once while the answer for it is on its way.
// `answer` is null for a pool with no answer and for a hidden player.
const poolFigure = (answer, field, lock) => {
  if (!answer) return { amount: null, locked: false };
  if (lock !== undefined) return { amount: lock, locked: true };
  return { amount: answer[field] ?? null, locked: false };
};

// Whether a pool's minimum raised an unlocked player's amount, and by how
// much (the up arrow). A locked amount is never marked: the coach's lock
// shows at once, before the answer that drops the raise lands.
const raiseOf = (answer, figure) => {
  const floored = !figure.locked && !!answer?.floored;
  return { floored, lift: floored ? Number(answer.lift) || 0 : 0 };
};

// The allocate answer (`allocation.rs` / `allocation.nil`, the endpoint's
// revshare and nil pools) on the table rows:
//  - revenue share: `allocated` and `rsLocked`, and `rsFloored` / `rsLift`
//    when the player's floor (his minimum, rsFloor) raised an unlocked amount;
//  - NIL: `nilPayout`, the recommended split (the NIL model over the
//    visible players with no locks and no minimums), and `nilAllocated`,
//    the amount with the locks and NILx minimums, with `nilLocked` and
//    `nilFloored` / `nilLift` (his NILx value raised it).
// Until a pool has an answer, and for a player hidden from it, its figures
// are null ("-").
export const mergeAllocation = (rows, allocation = {}, { rsLocks = {}, nilLocks = {} } = {}) =>
  rows.map((r) => {
    const id = rowId(r);
    const rsAnswer = allocation.rs && !r.hiddenRs ? allocation.rs.rows?.[id] || {} : null;
    const nilAnswer = allocation.nil && !r.hiddenNil ? allocation.nil.rows?.[id] || {} : null;
    const rs = poolFigure(rsAnswer, "payoutAllocated", rsLocks[id]);
    const nil = poolFigure(nilAnswer, "payout", nilLocks[id]);
    const rsRaise = raiseOf(rsAnswer, rs);
    const nilRaise = raiseOf(nilAnswer, nil);
    return {
      ...r,
      allocated: rs.amount,
      rsLocked: rs.locked,
      rsFloored: rsRaise.floored,
      rsLift: rsRaise.lift,
      nilPayout: nilAnswer?.recommended ?? null,
      nilAllocated: nil.amount,
      nilLocked: nil.locked,
      nilFloored: nilRaise.floored,
      nilLift: nilRaise.lift,
    };
  });

// Budget / allocated / remaining for one pool ("rs" or "nil"): the allocate
// answer's totals (`totals`, that pool of the answer) and the players still
// in the pool. `over` is the answer's overAllocated. A budget with no answer
// yet has no allocated or remaining figure (null, "-").
export const poolSummary = (rows, pool, budget, totals) => {
  const players = rows.filter((r) => (pool === "nil" ? !r.hiddenNil : !r.hiddenRs)).length;
  if (!totals) return { budget, allocated: budget > 0 ? null : 0, remaining: budget > 0 ? null : 0, players, over: false };
  return { budget, allocated: round2(Number(totals.allocatedTotal) || 0), remaining: round2(Number(totals.remaining) || 0), players, over: !!totals.overAllocated };
};

export const budgetPurposeOf = (rs, nil) => (rs > 0 && nil > 0 ? "both" : rs > 0 ? "revshare" : nil > 0 ? "nil" : "");

// Which pools a partly hidden player still belongs to.
export const poolTag = (row) => {
  if (row.hiddenRs && !row.hiddenNil) return "NIL only";
  if (!row.hiddenRs && row.hiddenNil) return "RevShare only";
  return "";
};

export const cleanPosition = (raw) => {
  const v = (raw || "").toString().trim();
  return !v || v.toLowerCase().includes("unknown") ? "" : v;
};

export const jerseyOf = (row) => {
  const v = (row?.jersey ?? "").toString().trim();
  return v === "-" ? "" : v;
};

// The class levels, in class order, as the Team tab writes them
// (coachUtils.classificationOf reads "Jr", "Junior", "11th" and the like).
const CLASS_LEVELS = ["FR", "SO", "JR", "SR", "GR"];
// "R-Jr", "RS Jr", "Redshirt Junior": a redshirt's class is his class.
const REDSHIRT = [/^redshirt[\s.-]*/i, /^rs[\s.-]*/i, /^r[\s.-]*/i];

const yearOf = (value) => {
  const text = String(value ?? "").trim();
  return /^\d{4}$/.test(text) ? text : "";
};

// A class word as FR / SO / JR / SR / GR, or "" for anything else: a
// roster status ("self-registered", "ACT", "pending"), Cerebro's "PSA".
const levelOf = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const words = [text, ...REDSHIRT.map((prefix) => text.replace(prefix, "")).filter((bare) => bare && bare !== text)];
  return words.map(classWordOf).find((level) => CLASS_LEVELS.includes(level)) || "";
};

const classOfText = (value) => yearOf(value) || levelOf(value);

// The Classification a row shows: the calculator's own (a grad year, or a
// class level for a college team), else the 4-digit grad year, else the
// class standing as a class level; "" (shown as "-") for anything else, so
// a roster status never reads as a class and "Jr" and "JR" are one class.
export const classificationOf = (row) => classOfText(row?.classification) || yearOf(Number.parseInt(row?.gradYear, 10)) || classOfText(row?.classStanding);

export const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] || ""}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase() || "?";
};

// "G · #1": position and jersey, skipping what the feed does not know.
export const playerMeta = (row) => [cleanPosition(row.position), jerseyOf(row) ? `#${jerseyOf(row)}` : ""].filter(Boolean).join(" · ");

// A 0-100 score (Sport score, Visibility score) as every screen shows the
// pillars (10/19, D11): out of 10 with one decimal, 63.8 -> "6.4", clamped
// to 0-100 and rounded like the profile (Math.round of the 0-100 value).
// `suffix` adds "/10" where no "(out of 10)" header says it ("6.4/10").
// "-" when the score is missing.
export const fmtScore = (v, { suffix = false } = {}) => {
  if (v === null || v === undefined || v === "" || !Number.isFinite(Number(v))) return EMPTY;
  const text = (Math.round(Math.min(Math.max(Number(v), 0), 100)) / 10).toFixed(1);
  return suffix ? `${text}/10` : text;
};

const uniqueSorted = (values, numeric = false) =>
  [...new Set(values.filter(Boolean))].sort((a, b) => (numeric ? Number(a) - Number(b) || a.localeCompare(b) : a.localeCompare(b)));

// Grad years first, oldest first, then the class levels in class order.
const classRank = (value) => (yearOf(value) ? Number(value) : 10000 + CLASS_LEVELS.indexOf(value));

export const filterOptions = (rows) => ({
  position: uniqueSorted(rows.map((r) => cleanPosition(r.position))),
  number: uniqueSorted(rows.map(jerseyOf), true),
  classification: [...new Set(rows.map(classificationOf).filter(Boolean))].sort((a, b) => classRank(a) - classRank(b)),
});

export const filterRows = (rows, { position = "", number = "", classification = "" } = {}) =>
  rows.filter(
    (r) =>
      (!position || cleanPosition(r.position) === position) &&
      (!number || jerseyOf(r) === number) &&
      (!classification || classificationOf(r) === classification),
  );

// Player search in the Add player modal: a position of the sport being
// searched ("bb" or "fb", the cascade's sport value) filters by position,
// anything else matches the name, so "De" is a name to a basketball coach
// and a position to a football coach. The backend keeps the same lists
// (live_data_router.BASKETBALL_POSITIONS / FOOTBALL_POSITIONS).
const BASKETBALL_POSITIONS = new Set(["PG", "SG", "SF", "PF", "G", "F", "C", "GF", "FC"]);
const FOOTBALL_POSITIONS = new Set(["QB", "RB", "FB", "WR", "TE", "OL", "OT", "OG", "DL", "DE", "DT", "LB", "CB", "S", "DB", "K", "P", "LS", "ATH"]);

export const isPositionQuery = (query, sport = "bb") =>
  (sport === "fb" ? FOOTBALL_POSITIONS : BASKETBALL_POSITIONS).has(String(query || "").trim().toUpperCase());

// Letters and digits only, lower case, accents dropped: "J.J. Sati-Grier"
// reads "jjsatigrier".
const squashName = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// A name query matches when every word of it is in the player's name, in
// any order, whatever the case, spaces and punctuation: "JJ Sati-Grier",
// "sati-grier jj", "Sati Grier" and "jj sati" all find "J.J. Sati-Grier".
// The backend's CACHE player search matches the same way
// (cacheshare_people_search.name_matches).
export const nameMatches = (name, query) => {
  const words = String(query || "")
    .split(/\s+/)
    .map(squashName)
    .filter(Boolean);
  const full = squashName(name);
  return words.length > 0 && words.every((w) => full.includes(w));
};

export const matchesQuery = (row, query, sport = "bb") => {
  const q = String(query || "").trim();
  if (!q) return true;
  if (isPositionQuery(q, sport)) return cleanPosition(row.position).toUpperCase() === q.toUpperCase();
  return nameMatches(row.playerName, q);
};

export const paginate = (rows, page, size) => {
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const current = Math.min(Math.max(1, page), pages);
  const start = (current - 1) * size;
  return {
    rows: rows.slice(start, start + size),
    page: current,
    pages,
    from: rows.length ? start + 1 : 0,
    to: Math.min(start + size, rows.length),
    total: rows.length,
  };
};

// One row shape for every calculator, roster preview and search result.
// Football rows keep their position group and rating, which the NIL model
// weighs; a CACHE athlete's row keeps his IRL score (weighed too), his NILx
// value (his NIL minimum), his CacheValue and his revenue share floor
// (rsFloor, the low end of his PerformanceValue, and rsFloorSource,
// "performanceValue", or "cacheValue" for a valuation from before it), all
// null for a player with no CACHE account. A row from a calculator that
// sends no rsFloor keeps none (undefined), so the allocate endpoint floors
// it on its CacheValue as before.
const floorFields = (item) => (item.rsFloor === undefined ? {} : { rsFloor: item.rsFloor ?? null, rsFloorSource: item.rsFloorSource ?? null });

export const toRow = (item) => ({
  avatar: item.profilePhoto || item.avatar || "",
  playerName: item.playerName || `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.name || "",
  email: item.email || "",
  sportsRadarId: rowId(item),
  position: item.position || "",
  positionGroup: item.positionGroup || null,
  rating: item.rating ?? null,
  jersey: item.jersey ?? "",
  status: item.status || "",
  teamName: item.teamName || "",
  classification: item.classification || "",
  gradYear: item.gradYear ?? null,
  classStanding: item.classStanding || "",
  sportScore: item.sportScore || 0,
  cacheScore: item.cacheScore || item.sportScore || 0,
  nilScore: item.nilScore ?? null,
  visibilityScore: item.visibilityScore ?? null,
  irlScore: item.irlScore ?? null,
  nilxValue: item.nilxValue ?? null,
  cacheValue: item.cacheValue ?? null,
  ...floorFields(item),
  isPreviousSeason: item.isPreviousSeason ?? item.redshirtSeason ?? false,
  seasonYear: item.seasonYear ?? null,
  // The score's season note, when a newer season's games have not yet
  // moved it (the PS chip's hover text, allocationCells.previousSeasonTitle).
  seasonTransition: item.seasonTransition || null,
  isRegistered: !!item.isRegistered,
  athleteId: item.athleteId || "",
  cacheStatus: item.cacheStatus || null,
  payoutTeam: item.payoutTeam ?? null,
  payoutAllPlayers: item.payoutAllPlayers ?? null,
});

// An imported player keeps where it came from so the calculator can score
// it against its own team and league.
export const toImportRow = (item, source) => ({
  ...toRow(item),
  source: item.source || source || "sportsradar",
  sourceSport: item.sourceSport || item.sport || "",
  sourceTeamId: item.sourceTeamId || "",
  sourceCompetitionId: item.sourceCompetitionId || null,
});

// The calculators' `importedAthletes` entry for an imported player.
export const importPayload = (p) => ({
  id: rowId(p),
  source: p.source || "sportsradar",
  sportScore: p.sportScore || 0,
  name: p.playerName || "",
  position: p.position || "",
  teamName: p.teamName || "",
  sport: p.sourceSport || "",
  sourceTeamId: p.sourceTeamId || "",
  sourceCompetitionId: p.sourceCompetitionId || null,
});

// Fold a fresh calculator row into an imported player without losing the
// import's source tags.
const SCORED_FIELDS = [
  "sportScore",
  "cacheScore",
  "nilScore",
  "visibilityScore",
  "irlScore",
  "nilxValue",
  "cacheValue",
  "rsFloor",
  "rsFloorSource",
  "rating",
  "positionGroup",
  "isPreviousSeason",
  "seasonYear",
  "seasonTransition",
  "classification",
  "gradYear",
  "classStanding",
  "payoutTeam",
  "payoutAllPlayers",
];

// A rescore's NILx value, CacheValue, revenue share floor and IRL score
// replace the saved ones even when they come back empty: each moves money
// (the NILx value and the IRL score NIL, the floor revenue share), so a
// value the athlete no longer has must stop counting. The season note is
// replaced the same way, so it never outlives the season it describes.
// Other fields keep their last value when a rescore leaves them blank, and
// a field the rescore does not send is left alone (a calculator that sends
// no rsFloor leaves the saved one).
const CURRENT_ONLY = new Set(["nilxValue", "cacheValue", "rsFloor", "rsFloorSource", "irlScore", "seasonTransition"]);

export const mergeScored = (player, scored) => {
  if (!scored) return player;
  const next = { ...player };
  SCORED_FIELDS.forEach((key) => {
    if (CURRENT_ONLY.has(key)) {
      if (scored[key] !== undefined) next[key] = scored[key] === "" ? null : scored[key];
    } else if (scored[key] !== undefined && scored[key] !== null && scored[key] !== "") next[key] = scored[key];
  });
  return next;
};

// ---- Coach program and session ------------------------------------------

// The switcher / session key of a coach team: one program can be several
// squads (17U / 16U / 15U) that share an id and differ by league.
export const teamKeyOf = (record) => `${record?.teamId || ""}|${record?.league || ""}`;

const SR_LEAGUE_KEYS = ["ncaamb", "ncaawb", "ncaad1mb", "ncaad1wb", "mbb", "wbb", "ncaafb"];

// A coach team from the Genius Sports football feed: the server says so
// (teamSource), or, on a record that names no source, NCAA football, which
// the coach team picker only lists from Genius. Its roster and stats come
// from the Genius calculator, not the cached SportRadar one. The server
// infers "sportsradar" for a football record saved without a source; a
// numeric team id there is still a Genius team (SportRadar ids are UUIDs).
const GENIUS_LEAGUE_KEYS = ["ncaafb"];
const NUMERIC_ID = /^\d+$/;
const isGeniusRecord = (record, league) => {
  const source = String(record.teamSource || record.team_source || "").toLowerCase();
  const football = GENIUS_LEAGUE_KEYS.includes(league.toLowerCase());
  if (source === "sportsradar" && football) return NUMERIC_ID.test(String(record.teamId).trim());
  return source ? source === "genius" : football;
};

// The calculator team for a coach team record: CACHE Partner leagues
// (cache_*), Genius football teams, SportRadar NCAA D-I leagues, else a live
// Cerebro squad. A Genius team sends no competition: the server serves the
// season it has data for.
export const lockedTeamOf = (record) => {
  if (!record?.teamId) return null;
  const league = record.league || "";
  const cache = league.toLowerCase().startsWith("cache_");
  const genius = !cache && isGeniusRecord(record, league);
  let source = "cerebro";
  if (cache) source = "cache_teams";
  else if (genius) source = "genius";
  else if (SR_LEAGUE_KEYS.includes(league.toLowerCase())) source = "sr";
  return {
    id: record.teamId,
    name: record.currentTeam || "Your team",
    leagueName: record.cascade?.leagueDisplayName || league,
    cerebroLeague: cache || genius ? "" : league,
    cacheLeague: cache ? league : null,
    league_key: cache ? league : undefined,
    competitionId: genius ? record.competitionId || null : undefined,
    source,
  };
};

// ---- Data status (a provider's saved copy) -------------------------------

// What each provider's data is called on the page.
const PROVIDER_DATA = { genius: "football stats" };
const AS_OF = { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" };

// "Aug 27, 2026" for the server's as-of time, or "".
const asOfText = (iso) => {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString("en-US", AS_OF) : "";
};

// The notice for a server dataStatus: "Showing 2025-26 football stats saved
// Aug 27, 2026. Live updates are paused." while the provider is paused and
// saved data stands in, plus how many added players have no saved stats.
// Built from the season label and as-of time the server sends, so it names
// whatever season is served. A team list the team feed did not fully answer
// carries no provider: it says so in the server's own neutral sentence
// ("Team search is limited right now. Some teams may be missing.", 10/19
// notes, J1). "" when there is nothing to say.
export const dataStatusNotice = (status) => {
  if (!status) return "";
  if (!status.provider && status.state !== "live") {
    const message = typeof status.message === "string" ? status.message.trim() : "";
    if (message) return message;
  }
  const parts = [];
  if (status.state === "stale") {
    const what = PROVIDER_DATA[status.provider] || "stats";
    const season = status.season?.label ? `${status.season.label} ` : "";
    const saved = asOfText(status.asOf);
    parts.push(`Showing ${season}${what}${saved ? ` saved ${saved}` : ""}. Live updates are paused.`);
  }
  const missing = (status.missingImports || []).length;
  if (missing) parts.push(`${missing} added player${missing === 1 ? " has" : "s have"} no saved stats.`);
  return parts.join(" ");
};

// The dataStatus a team list carries: the list's own (a list the team feed
// limited keeps it even with no team in it: cacheShareServices), else its
// teams' (each team is tagged with it).
export const teamsDataStatus = (teams) => teams?.dataStatus || (teams || []).find((t) => t?.dataStatus)?.dataStatus || null;

// Sport ("bb" | "fb") and gender ("M" | "F" | null) of a coach program, from
// the cascade labels saved at onboarding, else the league key.
export const programSportGender = (cascade = {}, league = "") => {
  const lg = String(league || "").toLowerCase();
  const sportLabel = String(cascade.sport || "").toLowerCase();
  const sport = sportLabel.includes("football") || /fb$/.test(lg) ? "fb" : "bb";
  if (sport === "fb") return { sport, gender: null };
  const genderLabel = String(cascade.gender || "").toLowerCase();
  const female = genderLabel ? /women|girl/.test(genderLabel) : /(wb|wbb)$|girls|women/.test(lg);
  return { sport, gender: female ? "F" : "M" };
};

// The saved CacheShare state of one coach team. Sessions saved before teams
// had their own slice live at the top level and belong to the primary team.
export const sessionSliceFor = (doc, record) => {
  const slice = doc?.teams?.[teamKeyOf(record)];
  if (slice) return slice;
  if (record?.isPrimary && (doc?.revenueAmount || doc?.addedPlayers?.length)) {
    return { revenueAmount: doc.revenueAmount || "", addedPlayers: doc.addedPlayers || [] };
  }
  return {};
};
